import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Fehlerfang from "@/components/Fehlerfang";
import { lazy, Suspense, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import useHandy from "@/hooks/useHandy";

/** Jede Seite kommt als eigenes Paket erst dann vom Server, wenn sie zum
 *  ersten Mal aufgerufen wird. So bleibt das Grundpaket klein und die
 *  Anwendung ist nach dem Anmelden sofort bedienbar. Startseite und Anmeldung
 *  bleiben im Grundpaket, weil sie ohnehin immer gebraucht werden. */
function SeiteLaedt() {
  return (
    <div className="flex items-center justify-center py-16" data-testid="seite-laedt">
      <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
    </div>
  );
}

import Login from "@/pages/Login";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
const ImportPage = lazy(() => import("@/pages/ImportPage"));
const CustomersPage = lazy(() => import("@/pages/CustomersPage"));
const KundenstatusStatistik = lazy(() => import("@/pages/KundenstatusStatistik"));
const CustomerDetail = lazy(() => import("@/pages/CustomerDetail"));
const UnknownContacts = lazy(() => import("@/pages/UnknownContacts"));
const Telefonbuch = lazy(() => import("@/pages/Telefonbuch"));
const Telefonate = lazy(() => import("@/pages/Telefonate"));
const Wallboard = lazy(() => import("@/pages/Wallboard"));
const WallboardHandy = lazy(() => import("@/pages/WallboardHandy"));
const Meldungen = lazy(() => import("@/pages/Meldungen"));
const TelefonatStatistik = lazy(() => import("@/pages/TelefonatStatistik"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const Duplicates = lazy(() => import("@/pages/Duplicates"));
const Reminders = lazy(() => import("@/pages/Reminders"));
const Postfach = lazy(() => import("@/pages/Postfach"));
const Aktivitaeten = lazy(() => import("@/pages/Aktivitaeten"));
const Vertriebsarbeit = lazy(() => import("@/pages/Vertriebsarbeit"));
import { Navigate as NavigateStart } from "react-router-dom";
import { useAuth as useAuthStart } from "@/context/AuthContext";

/** Wer einen eigenen Startbildschirm gewaehlt hat, landet dort - alle
 *  anderen auf dem Dashboard. */
// Beim Oeffnen des Programms ist das Wallboard fuer alle die erste Seite
// (Franklin, 24.09.2026) - ohne persoenliche Ausnahme. "Oeffnen" heisst: ein
// neuer Browser-Tab bzw. eine neue Sitzung; einmal je Tab. Danach fuehrt ein
// Klick auf "Dashboard" ganz normal zum Dashboard.
const TAFEL_MARKE = "cadmill_tafel_gezeigt";
export function tafelZuerst() {
  try {
    if (sessionStorage.getItem(TAFEL_MARKE)) return false;
    sessionStorage.setItem(TAFEL_MARKE, "1");
    return true;
  } catch {
    return false;
  }
}

function StartWeiche() {
  if (tafelZuerst()) return <NavigateStart to="/tafel" replace />;
  return <Dashboard />;
}

/** Wer das Programm auf einer Menueseite oeffnet (Lesezeichen, wieder-
 *  hergestellter Tab), sieht trotzdem zuerst das Wallboard. Tiefe Adressen
 *  bleiben, wie sie sind: ein Link auf eine Kundenakte, eine Mail oder ein
 *  Gespraech (aus Aufgaben, Kalender, Postfach) muss dort landen. */
function TafelZuerst() {
  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();
  useEffect(() => {
    if (!tafelZuerst()) return;
    const tief = pathname.split("/").filter(Boolean).length > 1 || !!search || !!hash;
    if (!tief && pathname !== "/tafel") navigate("/tafel", { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

/** Die Tafel fuer Menschen: am Rechner das Wallboard im Rahmen (60-Zoll-
 *  Aufteilung, Ziehen nach "erledigt"), auf dem Handy die Handy-Tafel -
 *  gleiche Daten, gleiche Handgriffe (lib/tafel.js), andere Oberflaeche.
 *  Die Grenze ist die Fensterbreite (hooks/useHandy.js), nicht das Geraet. */
function TafelWeiche() {
  const handy = useHandy();
  return handy ? <WallboardHandy /> : <Wallboard eingebettet />;
}

/** Meldungen oben rechts am Rechner; auf dem Handy oben mittig, wo der
 *  Daumen nicht gerade ist und nichts verdeckt wird. */
function Meldeleiste() {
  const handy = useHandy();
  return <Toaster position={handy ? "top-center" : "top-right"} />;
}

/** /wallboard ist die Adresse des Wandbildschirms (Kiosk-PC, ohne Menue).
 *  Ein Mensch, der sie aufruft, kommt zur eingebetteten Fassung. */
function WallboardWeiche() {
  const { istWallboard } = useAuthStart();
  if (istWallboard) return <Suspense fallback={<SeiteLaedt />}><Wallboard /></Suspense>;
  return <NavigateStart to="/tafel" replace />;
}
const Revenue = lazy(() => import("@/pages/Revenue"));
const ZuordnungsKontrolle = lazy(() => import("@/pages/ZuordnungsKontrolle"));
const Fruehwarnung = lazy(() => import("@/pages/Fruehwarnung"));
const Einstellungen = lazy(() => import("@/pages/Einstellungen"));
const ProduktTrends = lazy(() => import("@/pages/ProduktTrends"));
const UserManagement = lazy(() => import("@/pages/UserManagement"));
const VerloreneUmsaetze = lazy(() => import("@/pages/VerloreneUmsaetze"));
const MaterialStatistik = lazy(() => import("@/pages/MaterialStatistik"));
const ProduktStatistik = lazy(() => import("@/pages/ProduktStatistik"));
const AbwanderungsAnalyse = lazy(() => import("@/pages/AbwanderungsAnalyse"));
const HubspotZuordnung = lazy(() => import("@/pages/HubspotZuordnung"));
const Agenda2026 = lazy(() => import("@/pages/Agenda2026"));
const BestellenKomet = lazy(() => import("@/pages/BestellenKomet"));
const Bestellungen = lazy(() => import("@/pages/Bestellungen"));
const BestellenImplantate = lazy(() => import("@/pages/BestellenImplantate"));
const Wettbewerb = lazy(() => import("@/pages/Wettbewerb"));
const Agenten = lazy(() => import("@/pages/Agenten"));
const Werbung = lazy(() => import("@/pages/Werbung"));

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

/**
 * Sperrt alles außer dem Wallboard aus, wenn das Bildschirm-Konto angemeldet
 * ist. Serverseitig ist es ohnehin gesperrt – ohne diese Weiche sähe man
 * aber eine Seite, auf der jede einzelne Anfrage mit 403 endet, und niemand
 * wüsste warum.
 */
function NurFuerMenschen({ children }) {
  const { istWallboard } = useAuth();
  if (istWallboard) return <Navigate to="/wallboard" replace />;
  return children;
}

/**
 * Wer angemeldet ist, hat auf der Anmeldeseite nichts mehr zu suchen.
 * Ohne diese Weiterleitung bleibt man nach erfolgreichem Login auf /login
 * stehen – die Sitzung besteht dann zwar, sichtbar passiert aber nichts.
 */
function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }
  return user ? <Navigate to="/" replace /> : <Login />;
}

function RevenueRoute({ children }) {
  const { canRevenue } = useAuth();
  return canRevenue ? children : <Navigate to="/" replace />;
}

function ManagerRoute({ children }) {
  const { canManage } = useAuth();
  return canManage ? children : <Navigate to="/" replace />;
}

/** Bereiche, die man ausdrücklich bekommen muss (Benutzer & Rechte). */
function BereichRoute({ bereich, children }) {
  const { darfBereich } = useAuth();
  return darfBereich(bereich) ? children : <Navigate to="/" replace />;
}

function AppRouter() {
  // Der frühere Emergent-Redirect mit #session_id entfällt: Google Identity
  // Services liefert das Token direkt per Callback, ohne Umweg über die URL.
  return (
    <Routes>
      <Route path="/login" element={<LoginRoute />} />
      {/* Der Wandbildschirm laeuft ohne Menue und Rahmen - er wird gelesen,
          nicht bedient. Angemeldet sein muss er trotzdem. */}
      <Route path="/wallboard" element={<ProtectedRoute><WallboardWeiche /></ProtectedRoute>} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <NurFuerMenschen>
              <TafelZuerst />
              <AppLayout />
            </NurFuerMenschen>
          </ProtectedRoute>
        }
      >
        <Route index element={<StartWeiche />} />
        <Route path="agenda" element={<Agenda2026 />} />
        {/* Das Wallboard fuer Menschen: im Rahmen, mit Menue links, zum
            Klicken, Ziehen und Uebernehmen. Der Kiosk bleibt auf /wallboard. */}
        <Route path="tafel" element={<TafelWeiche />} />
        <Route path="fruehwarnung" element={<RevenueRoute><Fruehwarnung /></RevenueRoute>} />
        <Route path="produkt-trends" element={<RevenueRoute><ProduktTrends /></RevenueRoute>} />
        <Route path="material" element={<RevenueRoute><MaterialStatistik /></RevenueRoute>} />
        <Route path="produkte" element={<RevenueRoute><ProduktStatistik /></RevenueRoute>} />
        <Route path="kunden" element={<CustomersPage />} />
        <Route path="kundenstatus" element={<KundenstatusStatistik />} />
        <Route path="kunden/:id" element={<CustomerDetail />} />
        <Route path="umsatz" element={<RevenueRoute><Revenue /></RevenueRoute>} />
        <Route path="zuordnung" element={<RevenueRoute><ZuordnungsKontrolle /></RevenueRoute>} />
        <Route path="hubspot-zuordnung" element={<RevenueRoute><HubspotZuordnung /></RevenueRoute>} />
        <Route path="verloren" element={<RevenueRoute><VerloreneUmsaetze /></RevenueRoute>} />
        <Route path="abwanderung" element={<RevenueRoute><AbwanderungsAnalyse /></RevenueRoute>} />
        <Route path="benutzer" element={<ManagerRoute><UserManagement /></ManagerRoute>} />
        <Route path="unbekannt" element={<UnknownContacts />} />
        <Route path="telefonbuch" element={<Telefonbuch />} />
        <Route path="telefonate" element={<Telefonate />} />
        <Route path="telefonate/auswertung"
               element={<ManagerRoute><TelefonatStatistik /></ManagerRoute>} />
        <Route path="dubletten" element={<Duplicates />} />
        {/* Zwei Arten, zwei Menuepunkte. Der alte Pfad leitet weiter,
            damit Lesezeichen und Verlaeufe nicht ins Leere gehen. */}
        <Route path="postfach" element={<Postfach />} />
        <Route path="aktivitaeten" element={<Aktivitaeten />} />
        <Route path="vertriebsarbeit" element={<RevenueRoute><Vertriebsarbeit /></RevenueRoute>} />
        {/* Implantat-Bauteile haben einen Katalog statt eines Scanners - eigene
            Seite, die vor der Scan-Route stehen muss, sonst faengt :lieferant sie. */}
        <Route path="bestellen/implantate" element={<BestellenImplantate />} />
        <Route path="bestellen/:lieferant" element={<BestellenKomet />} />
        <Route path="bestellungen" element={<Bestellungen />} />
        <Route path="aufgaben-kundenservice" element={<Reminders artFest="kundenservice" />} />
        <Route path="aufgaben-vertrieb" element={<Reminders artFest="vertrieb" />} />
        <Route path="erinnerungen" element={<Navigate to="/aufgaben-kundenservice" replace />} />
        <Route path="einstellungen" element={<RevenueRoute><Einstellungen /></RevenueRoute>} />
        <Route path="einstellungen/:bereich" element={<RevenueRoute><Einstellungen /></RevenueRoute>} />
        <Route path="analyse" element={<Analytics />} />
        {/* Markt & Wettbewerb: Reiter in der Adresse, damit man einen Blick
            (z. B. /wettbewerb/preise) als Lesezeichen ablegen kann. */}
        <Route path="wettbewerb" element={<BereichRoute bereich="wettbewerb"><Wettbewerb /></BereichRoute>} />
        <Route path="wettbewerb/:reiter" element={<BereichRoute bereich="wettbewerb"><Wettbewerb /></BereichRoute>} />
        {/* Agenten-Leitstand: alle Automationen mit Ampel und Handgriffen */}
        <Route path="agenten" element={<BereichRoute bereich="agenten"><Agenten /></BereichRoute>} />
        {/* E-Mail-Kampagnen ueber Brevo: Reiter in der Adresse, eine Kampagne
            unter /werbung/kampagnen/<id>. Nur mit Bereich "werbung". */}
        <Route path="werbung" element={<BereichRoute bereich="werbung"><Werbung /></BereichRoute>} />
        <Route path="werbung/kampagnen/:kampagneId" element={<BereichRoute bereich="werbung"><Werbung reiterFest="kampagnen" /></BereichRoute>} />
        <Route path="werbung/:reiter" element={<BereichRoute bereich="werbung"><Werbung /></BereichRoute>} />
        {/* Push-Meldungen auf dem Handy: einschalten, auswaehlen, testen. */}
        <Route path="meldungen" element={<Meldungen />} />
        <Route path="import" element={<ImportPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    // Der Fehlerfang liegt ganz aussen: Wirft irgendeine Komponente beim
    // Rendern, haengt React sonst den gesamten Baum ab und es bleibt eine
    // weisse Seite. Innerhalb des Fangs steht stattdessen die Meldung.
    <Fehlerfang>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Meldeleiste />
        </BrowserRouter>
      </AuthProvider>
    </Fehlerfang>
  );
}
